import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, authedFileUrl } from "../api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Upload, AlertTriangle, KeyRound, User } from "lucide-react";
import { CustomFieldsManager } from "../components/CustomFields";

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [settings, setSettings] = useState(null);
  const [logoVersion, setLogoVersion] = useState(0);
  const [resetPhrase, setResetPhrase] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetDtPhrase, setResetDtPhrase] = useState("");
  const [resettingDt, setResettingDt] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [unNew, setUnNew] = useState("");
  const [unPw, setUnPw] = useState("");
  const [unSaving, setUnSaving] = useState(false);
  const fileRef = useRef(null);

  const changeUsername = async () => {
    const trimmed = (unNew || "").trim().toLowerCase();
    if (!trimmed || !unPw) {
      toast.error("Enter a new username and your current password");
      return;
    }
    if (trimmed.length < 3) {
      toast.error("Username must be at least 3 characters");
      return;
    }
    if (trimmed === user?.username) {
      toast.error("That's already your username");
      return;
    }
    setUnSaving(true);
    try {
      await api.post("/auth/change-username", {
        current_password: unPw,
        new_username: trimmed,
      });
      await refreshUser();
      toast.success(`Username updated to "${trimmed}" — use it next time you sign in`);
      setUnNew(""); setUnPw("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change username");
    } finally {
      setUnSaving(false);
    }
  };

  const changePassword = async () => {
    if (!pwCurrent || !pwNew) {
      toast.error("Fill in both password fields");
      return;
    }
    if (pwNew.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (pwNew !== pwConfirm) {
      toast.error("New passwords do not match");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwCurrent,
        new_password: pwNew,
      });
      toast.success("Password updated — use it next time you sign in");
      setPwCurrent(""); setPwNew(""); setPwConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change password");
    } finally {
      setPwSaving(false);
    }
  };

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    try {
      await api.put("/settings", {
        company_name: settings.company_name,
        address: settings.address,
        phone: settings.phone,
        ein: settings.ein,
        logo_path: settings.logo_path,
        email: settings.email || "",
        city: settings.city || "",
        state: settings.state || "",
        zip: settings.zip || "",
      });
      toast.success("Settings saved");
    } catch (e) { toast.error("Save failed"); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSettings((s) => ({ ...s, logo_path: r.data.logo_path }));
      setLogoVersion((v) => v + 1);
      toast.success("Logo uploaded");
    } catch (err) { toast.error("Upload failed"); }
  };

  const resetAll = async () => {
    if (resetPhrase !== "RESET ALL DATA") {
      toast.error('Type "RESET ALL DATA" exactly to confirm');
      return;
    }
    if (!window.confirm(
      "Last warning: this permanently deletes every driver, trip, pay statement, deduction template and audit log. "
      + "Your login + company profile + logo are kept. There is NO undo."
    )) return;
    setResetting(true);
    try {
      const r = await api.post("/admin/reset-all", { confirm: "RESET ALL DATA" });
      const d = r.data.deleted || {};
      toast.success(
        `Reset complete · drivers ${d.drivers || 0} · trips ${d.trips || 0} · statements ${d.pay_statements || 0} · templates ${d.deduction_templates || 0} · audit ${d.audit_logs || 0}`
      );
      setResetPhrase("");
      // Force a full reload so every page's cached data gets re-fetched empty.
      setTimeout(() => window.location.assign("/"), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const resetDriversTrips = async () => {
    if (resetDtPhrase !== "RESET DRIVERS AND TRIPS") {
      toast.error('Type "RESET DRIVERS AND TRIPS" exactly to confirm');
      return;
    }
    if (!window.confirm(
      "Last warning: this permanently deletes every driver, every trip, every pay statement AND every driver expense for this workspace. "
      + "Deduction templates, settings and your login are kept. There is NO undo."
    )) return;
    setResettingDt(true);
    try {
      const r = await api.post("/admin/reset-drivers-trips", { confirm: "RESET DRIVERS AND TRIPS" });
      const d = r.data.deleted || {};
      toast.success(`Reset complete · drivers ${d.drivers || 0} · trips ${d.trips || 0} · statements ${d.pay_statements || 0} · expenses ${d.expenses || 0}`);
      setResetDtPhrase("");
      setTimeout(() => window.location.assign("/"), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setResettingDt(false);
    }
  };

  const resetCompanyProfile = async () => {
    if (!window.confirm(
      "Wipe company name, address, phone, email, EIN and the uploaded logo?\n\n"
      + "Use this to white-label the app before handing it to a buyer. "
      + "Drivers, trips, pay statements and your login are kept."
    )) return;
    try {
      await api.post("/admin/reset-company-profile");
      toast.success("Company profile wiped — Settings page is blank now");
      // Hard reload so the Layout + Login brand data refetches.
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  if (!settings) return <div className="text-slate-500">Loading...</div>;

  const isBlank = !(settings.company_name || "").trim();
  const logoUrl = settings.logo_path ? `${authedFileUrl("/settings/logo")}&v=${logoVersion}` : null;

  const permissions = [
    "Manage users and invitations", "Delete records", "Edit company settings",
    "Edit driver pay rates", "View and manage the audit trail", "Approve and delete pay runs",
    "Manage drivers and trips", "Build and run payroll", "Run reports and exports",
  ];

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Settings</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Company profile and payroll defaults. Everything here flows into the header, pay statements and every export.
          </p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={save} data-testid="save-settings-btn">Save settings</Button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="eyebrow">Account &amp; Access</div>
        <div className="text-xs text-slate-500 mt-0.5">Your signed-in account and what your current role is allowed to do.</div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          {permissions.map((p) => (
            <div key={p} className="flex items-center justify-between rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
              <span>{p}</span><span className="pill-green">Allowed</span>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="eyebrow">Company Profile</div>
        <div className="text-xs text-slate-500 mt-0.5">Shown in the header bar and on printed reports and statements.</div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Company Name *</Label><Input value={settings.company_name || ""} onChange={(e) => setSettings({ ...settings, company_name: e.target.value })} data-testid="company-name" /></div>
          <div><Label>Phone</Label><Input value={settings.phone || ""} onChange={(e) => setSettings({ ...settings, phone: e.target.value })} data-testid="company-phone" /></div>
          <div><Label>Street Address</Label><Input value={settings.address || ""} onChange={(e) => setSettings({ ...settings, address: e.target.value })} data-testid="company-address" /></div>
          <div><Label>Email</Label><Input value={settings.email || ""} onChange={(e) => setSettings({ ...settings, email: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-3 md:col-span-2">
            <div><Label>City</Label><Input value={settings.city || ""} onChange={(e) => setSettings({ ...settings, city: e.target.value })} /></div>
            <div><Label>State</Label><Input maxLength={2} value={settings.state || ""} onChange={(e) => setSettings({ ...settings, state: e.target.value.toUpperCase() })} /></div>
            <div><Label>ZIP</Label><Input value={settings.zip || ""} onChange={(e) => setSettings({ ...settings, zip: e.target.value })} /></div>
          </div>
          <div className="md:col-span-2"><Label>EIN / Company Codes</Label><Input value={settings.ein || ""} onChange={(e) => setSettings({ ...settings, ein: e.target.value })} data-testid="company-ein" /></div>
        </div>

        <div className="mt-6 border-t border-slate-100 pt-4">
          <div className="font-semibold text-sm">Company logo (optional)</div>
          <div className="text-xs text-slate-500 mt-0.5">PNG, JPG or SVG under 5 MB. Appears on PDFs and in the header.</div>
          <div className="mt-3 flex items-center gap-4">
            <div className="w-24 h-24 rounded border border-dashed border-slate-300 bg-slate-50 grid place-items-center overflow-hidden">
              {logoUrl ? <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" /> : <div className="text-slate-400 text-[10px]">No logo</div>}
            </div>
            <div>
              <Button onClick={() => fileRef.current?.click()} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="upload-logo-btn">
                <Upload size={14} className="mr-1" /> Upload logo
              </Button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="logo-file-input" />
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="change-username-card">
        <div className="flex items-center gap-2">
          <User size={16} className="text-slate-600" />
          <div className="eyebrow">Change Username</div>
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          Current username: <span className="font-mono text-slate-700">{user?.username || "—"}</span>. Letters, numbers, dot, underscore or dash.
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>New username</Label>
            <Input
              value={unNew}
              onChange={(e) => setUnNew(e.target.value)}
              autoComplete="username"
              placeholder="e.g. ruben.leal"
              data-testid="new-username-input"
            />
          </div>
          <div>
            <Label>Current password</Label>
            <Input
              type="password"
              value={unPw}
              onChange={(e) => setUnPw(e.target.value)}
              autoComplete="current-password"
              data-testid="current-password-for-username-input"
            />
          </div>
        </div>
        <div className="mt-4">
          <Button
            onClick={changeUsername}
            disabled={unSaving}
            className="bg-blue-600 hover:bg-blue-700 text-white disabled:bg-blue-300"
            data-testid="change-username-btn"
          >
            {unSaving ? "Updating…" : "Update username"}
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="change-password-card">
        <div className="flex items-center gap-2">
          <KeyRound size={16} className="text-slate-600" />
          <div className="eyebrow">Change Password</div>
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          Update the password you use to sign in. Minimum 6 characters.
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label>Current password</Label>
            <Input
              type="password"
              value={pwCurrent}
              onChange={(e) => setPwCurrent(e.target.value)}
              autoComplete="current-password"
              data-testid="current-password-input"
            />
          </div>
          <div>
            <Label>New password</Label>
            <Input
              type="password"
              value={pwNew}
              onChange={(e) => setPwNew(e.target.value)}
              autoComplete="new-password"
              data-testid="new-password-input"
            />
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input
              type="password"
              value={pwConfirm}
              onChange={(e) => setPwConfirm(e.target.value)}
              autoComplete="new-password"
              data-testid="confirm-password-input"
            />
          </div>
        </div>
        <div className="mt-4">
          <Button
            onClick={changePassword}
            disabled={pwSaving}
            className="bg-blue-600 hover:bg-blue-700 text-white disabled:bg-blue-300"
            data-testid="change-password-btn"
          >
            {pwSaving ? "Updating…" : "Update password"}
          </Button>
        </div>
      </div>

      <CustomFieldsManager />

      <div className="rounded-lg border-2 border-red-300 bg-red-50 p-5" data-testid="danger-zone">
        <div className="flex items-start gap-3">
          <AlertTriangle size={20} className="text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1 space-y-5">
            <div>
              <div className="eyebrow text-red-800">White-label — reset company profile</div>
              <div className="text-sm text-slate-700 mt-1">
                Wipes company name, address, phone, email, EIN and the uploaded logo. Keeps drivers, trips, statements and your login.
              </div>
              <Button
                onClick={resetCompanyProfile}
                className="mt-2 bg-red-600 hover:bg-red-700 text-white"
                data-testid="reset-company-btn"
              >
                Reset company profile
              </Button>
            </div>
            <div className="pt-4 border-t border-red-200">
              <div className="eyebrow text-red-800">Clear operational data — Drivers, trips & everything tied to them</div>
              <div className="text-sm text-slate-700 mt-1">
                Permanently deletes every <strong>driver, trip, pay statement and driver expense</strong> for this workspace. <strong>Deduction templates, settings, logo and your login are kept.</strong> <strong className="text-red-700">This cannot be undone.</strong>
              </div>
              <div className="mt-3 flex flex-col sm:flex-row gap-3 sm:items-end">
                <div className="flex-1 max-w-md">
                  <Label className="text-slate-900 font-semibold text-xs">Type <code className="font-mono bg-white px-1 rounded border border-red-200">RESET DRIVERS AND TRIPS</code> to enable the button</Label>
                  <Input
                    value={resetDtPhrase}
                    onChange={(e) => setResetDtPhrase(e.target.value)}
                    placeholder="RESET DRIVERS AND TRIPS"
                    className="font-mono"
                    data-testid="reset-dt-confirm-input"
                  />
                </div>
                <Button
                  onClick={resetDriversTrips}
                  disabled={resettingDt || resetDtPhrase !== "RESET DRIVERS AND TRIPS"}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white"
                  data-testid="reset-drivers-trips-btn"
                >
                  {resettingDt ? "Deleting…" : "Delete drivers & trips"}
                </Button>
              </div>
            </div>
            <div className="pt-4 border-t border-red-200">
              <div className="eyebrow text-red-800">Danger Zone — Reset all data</div>
              <div className="text-sm text-slate-700 mt-1">
                Permanently deletes every <strong>driver, trip, pay statement, deduction template, and audit log</strong>. Your login, company profile, logo and payroll defaults are kept so you can start fresh. <strong className="text-red-700">This cannot be undone.</strong>
              </div>
              <div className="mt-3 flex flex-col sm:flex-row gap-3 sm:items-end">
                <div className="flex-1 max-w-md">
                  <Label className="text-slate-900 font-semibold text-xs">Type <code className="font-mono bg-white px-1 rounded border border-red-200">RESET ALL DATA</code> to enable the button</Label>
                  <Input
                    value={resetPhrase}
                    onChange={(e) => setResetPhrase(e.target.value)}
                    placeholder="RESET ALL DATA"
                    className="font-mono"
                    data-testid="reset-confirm-input"
                  />
                </div>
                <Button
                  onClick={resetAll}
                  disabled={resetting || resetPhrase !== "RESET ALL DATA"}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white"
                  data-testid="reset-all-btn"
                >
                  {resetting ? "Resetting…" : "Reset app to zero"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
